import { useCallback } from "react"
import { getAuth } from "firebase/auth"
import { firebaseApp } from "~/client/firebase.client"
import { AUTH_PATHS } from "~/lib/consts"

/**
 * Global lock to prevent concurrent token refresh attempts.
 * When multiple API calls fail with 401 simultaneously, they all wait for
 * a single refresh operation instead of each attempting their own refresh.
 */
let tokenRefreshPromise: Promise<string> | null = null

export function useApi() {
  const apiFetch = useCallback(async (
    url: string,
    options?: RequestInit & { skipAuthRedirect?: boolean }
  ): Promise<Response> => {
    const { skipAuthRedirect, ...fetchOptions } = options || {}
    const response = await fetch(url, fetchOptions)

    if (response.status === 401 && !skipAuthRedirect) {
      return handleAuthError(url, fetchOptions)
    }

    // Handle unexpected HTML redirects (e.g. server redirected to login page)
    const contentType = response.headers.get("content-type")
    if (!response.ok && !contentType?.includes("application/json")) {
      redirectToLogin()
      throw new Error("Session expired")
    }

    return response
  }, [])

  const get = useCallback(
    (url: string, options?: RequestInit & { skipAuthRedirect?: boolean }) => {
      return apiFetch(url, { ...options, method: "GET" })
    },
    [apiFetch]
  )

  const post = useCallback(
    (
      url: string,
      body: BodyInit,
      options?: RequestInit & { skipAuthRedirect?: boolean },
    ) => {
      return apiFetch(url, { ...options, method: "POST", body })
    },
    [apiFetch],
  )

  const put = useCallback(
    (
      url: string,
      body: BodyInit,
      options?: RequestInit & { skipAuthRedirect?: boolean },
    ) => {
      return apiFetch(url, { ...options, method: "PUT", body })
    },
    [apiFetch],
  )

  const patch = useCallback(
    (
      url: string,
      body: BodyInit,
      options?: RequestInit & { skipAuthRedirect?: boolean },
    ) => {
      return apiFetch(url, { ...options, method: "PATCH", body })
    },
    [apiFetch],
  )

  const del = useCallback(
    (
      url: string,
      body: BodyInit,
      options?: RequestInit & { skipAuthRedirect?: boolean },
    ) => {
      return apiFetch(url, { ...options, method: "DELETE", body })
    },
    [apiFetch],
  )

  return {
    apiFetch,
    get,
    post,
    put,
    patch,
    delete: del,
  }
}

/**
 * Handles 401 errors by attempting to refresh the token
 */
const handleAuthError = async (
  url: string, 
  options: RequestInit | undefined
): Promise<Response> => {
  const auth = getAuth(firebaseApp)
  const user = auth.currentUser
  
  if (!user) {
    redirectToLogin()
    throw new Error("Session expired")
  }
  
  try {
    // Use global lock to coordinate concurrent refresh attempts
    if (!tokenRefreshPromise) {
      tokenRefreshPromise = (async () => {
        try {
          return await performTokenRefresh(user)
        } finally {
          tokenRefreshPromise = null
        }
      })()
    }
    
    // Wait for refresh
    await tokenRefreshPromise
    
    // Retry original request
    const retryResponse = await fetch(url, options)
    
    // If it still fails with 401, give up
    if (retryResponse.status === 401) {
      redirectToLogin()
      throw new Error("Session expired")
    }

    return retryResponse
  } catch (error) {
    console.error("Failed to refresh token:", error)
    redirectToLogin()
    throw new Error("Session expired")
  }
}

/**
 * Performs the actual token refresh against Firebase and syncs with the server
 */
const performTokenRefresh = async (user: any): Promise<string> => {
  // Force refresh the ID token from Firebase
  const idToken = await user.getIdToken(true)
  
  // Sync the new token to the server session
  const formData = new FormData()
  formData.set("idToken", idToken)
  
  const sessionResponse = await fetch("/resources/session", {
    method: "POST",
    body: formData,
  })
  
  // Check if session sync was successful
  const contentType = sessionResponse.headers.get("content-type")
  if (!sessionResponse.ok || (contentType && !contentType.includes("application/json"))) {
    console.error("Session sync failed or redirected:", sessionResponse.status, contentType)
    throw new Error(`Session sync failed: ${sessionResponse.status}`)
  }
  
  return idToken
}

/**
 * Helper to handle redirects to login with current path preservation.
 * Never use auth pages as redirectTo to prevent loops.
 */
const redirectToLogin = () => {
  if (typeof window === "undefined") return
  const currentPath = window.location.pathname
  if (AUTH_PATHS.includes(currentPath)) return
  window.location.href = `/refresh-token?redirectTo=${encodeURIComponent(currentPath)}`
}
